/**
 * SucursalVirtualPage — Inicio del portal de cliente.
 *
 * Secciones:
 *  1. Banner "Descarga la app" (dismissible, persiste en localStorage).
 *  2. Mascotas aseguradas (grid de PetCard + CTA "Agregar mascota").
 *  3. Cotizaciones pendientes (suscripciones con status PENDING).
 *  4. Club de Beneficios (preview — vista compacta linkeando a /beneficios).
 *
 * Datos: useMe, usePetsList, useSubscriptions (backend real).
 * Layout exterior viene de PortalLayout (sidebar + topbar).
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { PetCard } from '@app/components/PetCard';
import { EmptyState } from '@app/components/common/EmptyState';
import { useAuth } from '@modules/auth/presentation/hooks/useAuth';
import { useMe } from '@modules/users/presentation/hooks/useUsers';
import { usePetsList } from '@modules/pets/presentation/hooks/usePets';
import { useSubscriptions } from '@modules/billing/presentation/hooks/useBilling';
import { cn } from '@shared/utils/cn';
import {
  PawPrint,
  Plus,
  X,
  Smartphone,
  Apple,
  ArrowRight,
  BadgePercent,
} from 'lucide-react';

const DISMISS_KEY = 'mishiwoof_dismiss_download_banner_v1';

function DownloadAppBanner() {
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    setDismissed(localStorage.getItem(DISMISS_KEY) === '1');
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  if (dismissed) return null;

  return (
    <div className="relative flex flex-col items-start justify-between gap-4 rounded-2xl border border-border bg-card p-5 pr-10 shadow-xs sm:flex-row sm:items-center sm:p-6 sm:pr-12">
      <div className="flex items-start gap-3 sm:items-center sm:gap-4">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent text-accent-foreground sm:h-12 sm:w-12">
          <Smartphone className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={1.75} aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <p className="text-base font-semibold text-foreground">Descarga la app</p>
          <p className="text-sm text-muted-foreground">
            Haz tus reembolsos y accede a descuentos desde tu celular.
          </p>
        </div>
      </div>
      <div className="flex w-full shrink-0 flex-wrap gap-2 sm:w-auto">
        <Button
          variant="outline"
          size="sm"
          className="flex-1 rounded-lg sm:flex-none"
          disabled
          aria-label="Descargar para iOS — próximamente"
        >
          <Apple className="mr-1.5 h-4 w-4" aria-hidden="true" />
          App Store
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="flex-1 rounded-lg sm:flex-none"
          disabled
          aria-label="Descargar para Android — próximamente"
        >
          <Smartphone className="mr-1.5 h-4 w-4" aria-hidden="true" />
          Google Play
        </Button>
      </div>
      <button
        type="button"
        onClick={dismiss}
        className="absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        aria-label="Descartar este banner"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}

interface PendingQuoteCardProps {
  petName: string;
  description: string;
  onContinue: () => void;
  onDismiss?: () => void;
}

function PendingQuoteCard({ petName, description, onContinue, onDismiss }: PendingQuoteCardProps) {
  return (
    <div className="relative flex flex-col items-start justify-between gap-4 rounded-2xl border border-border bg-card p-5 shadow-xs sm:flex-row sm:items-center">
      <div className="flex items-center gap-4 pr-8 min-w-0">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-accent text-accent-foreground">
          <PawPrint className="h-6 w-6" strokeWidth={1.75} aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <p className="truncate text-base font-semibold text-foreground">{petName}</p>
          <p className="truncate text-sm text-muted-foreground">{description}</p>
        </div>
      </div>
      <Button variant="outline" size="sm" className="shrink-0 rounded-full" onClick={onContinue}>
        Seguir contratación
        <ArrowRight className="ml-1 h-3.5 w-3.5" aria-hidden="true" />
      </Button>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="absolute -right-2 -top-2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm transition-transform hover:scale-105"
          aria-label="Descartar cotización"
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

export function SucursalVirtualPage() {
  const { user: authUser } = useAuth();
  const { data: profile, isLoading: loadingProfile } = useMe();
  const { data: pets = [], isLoading: loadingPets } = usePetsList();
  const { data: subscriptions = [], isLoading: loadingSubs } = useSubscriptions();

  const displayName =
    profile?.firstName ?? profile?.lastName ?? authUser?.email ?? 'Usuario';

  const pendingQuotes = subscriptions.filter(
    (s) => String(s.status).toUpperCase() === 'PENDING',
  );

  return (
    <div className="space-y-6">
      {/* Saludo */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
          Hola, <span className="text-primary">{displayName.split(' ')[0]}</span>
        </h1>
        {loadingProfile ? (
          <Skeleton className="mt-2 h-4 w-48" />
        ) : (
          <p className="mt-1 text-sm text-muted-foreground">
            {profile?.email ?? authUser?.email}
          </p>
        )}
      </div>

      {/* Banner descarga app */}
      <DownloadAppBanner />

      {/* Mascotas aseguradas */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold tracking-tight text-foreground">
            Mascotas aseguradas
          </h2>
          <Button asChild className="rounded-full">
            <Link to="/contratacion">
              <Plus className="mr-1 h-4 w-4" aria-hidden="true" />
              Agregar mascota
            </Link>
          </Button>
        </div>

        {loadingPets ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-44 w-full rounded-2xl" />
            ))}
          </div>
        ) : pets.length === 0 ? (
          <EmptyState
            icon={PawPrint}
            title="Aún no agregaste mascotas"
            description="Cuando agregues a tu primera mascota, va a aparecer acá con su plan y estado de cobertura."
            action={
              <Button asChild className="rounded-full">
                <Link to="/contratacion">
                  <Plus className="mr-1 h-4 w-4" aria-hidden="true" />
                  Agregar mi primera mascota
                </Link>
              </Button>
            }
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {pets.map((pet) => (
              <Link
                key={pet.id}
                to={`/mascotas/${pet.id}`}
                className="rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                aria-label={`Ver detalle de ${pet.name}`}
              >
                <PetCard pet={pet} />
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Cotizaciones pendientes */}
      {!loadingSubs && pendingQuotes.length > 0 && (
        <section className="space-y-4">
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-bold tracking-tight text-foreground">
              Cotizaciones pendientes
            </h2>
            <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-xs font-bold text-destructive-foreground">
              {pendingQuotes.length}
            </span>
          </div>

          <div className="space-y-3">
            {pendingQuotes.map((s) => {
              const pet = pets.find((p) => p.id === s.petId);
              const petName = pet?.name ?? 'Tu mascota';
              const description = [pet?.breed, pet?.gender, pet?.age ? `${pet.age} año${pet.age === 1 ? '' : 's'}` : null]
                .filter(Boolean)
                .join(' · ');
              return (
                <PendingQuoteCard
                  key={s.id}
                  petName={petName}
                  description={description || (s.planName ?? 'Plan en proceso')}
                  onContinue={() => {
                    window.location.href = `/pagos?suscripcion=${s.id}`;
                  }}
                />
              );
            })}
          </div>
        </section>
      )}

      {/* Club de Beneficios — preview */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold tracking-tight text-foreground">
            Club de Beneficios
          </h2>
          <Link
            to="/beneficios"
            className="text-sm font-medium text-sidebar-primary hover:underline"
          >
            Ver todos
            <ArrowRight className="ml-1 inline h-3.5 w-3.5" aria-hidden="true" />
          </Link>
        </div>

        <div className="flex gap-3 overflow-x-auto pb-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className={cn(
                'flex h-32 w-32 shrink-0 flex-col items-center justify-center gap-2',
                'rounded-xl border border-dashed border-border bg-card text-center',
                'transition-shadow hover:shadow-md',
              )}
            >
              <BadgePercent
                className="h-7 w-7 text-muted-foreground"
                strokeWidth={1.5}
                aria-hidden="true"
              />
              <p className="px-2 text-xs text-muted-foreground">Partner próximamente</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
